import { Component, Input, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';

import { HelperService, HTTPService, DateService, FormatService, AttachmentService, DataService } from 'app/services';
import { Output, EventEmitter } from '@angular/core';
import { sortBy } from 'lodash';

@Component({
  selector: 'app-attachment-listing',
  templateUrl: './attachment-listing.component.html',
  styleUrls: ['./attachment-listing.component.scss'],
})
export class AttachmentListingComponent implements OnInit, OnDestroy {
  @Input() estimateId = '';
  @Input() estimateCode = '';
  @Input() isArchived: boolean = false;
  @Input() isReadyOnly: boolean = false;
  @Input() isFinalizedRo: boolean = false;

  _estimate: any = {};

  @Input('higlightedAttachmentId')
  set setHiglightedAttachmentId(higlightedAttachmentId) {
    this.higlightedAttachmentId = higlightedAttachmentId;
  }

  higlightedAttachmentId: string = '';

  // columns
  cols: any = [
    { field: 'title', header: 'File name', class: 'file-name' },
    { field: 'category', header: 'Type', class: 'file-type' },
    { field: 'fileType', header: 'Format', class: 'file-format' },
    { field: 'date', header: 'Uploaded on', class: 'file-upload' },
    { field: 'createdBy', header: 'Uploaded by', class: 'file-upload-by' },
    {
      field: 'isVisibleOnEstimate',
      header: 'Visible on Estimate',
      class: 'file-visible-estimate',
    },
    {
      field: 'isVisibleOnInvoice',
      header: 'Visible on Invoice',
      class: 'file-visible-invoice',
    },
    { field: 'delete', header: 'Delete', class: 'file-delete' },
  ];

  // attachment table details
  attachments = [];
  totalAttachments: number = 0;

  // search related fields
  isSearchSelectActive: boolean = false;
  searchResults: any[];
  searchQuery: string = '';
  isLoading: boolean = false;

  fileToUpload: File;

  // flags
  isNewAttachmentCreationDisabled: boolean = true;
  isCheckingNewAttachmentDisabled: boolean = false;
  isCreatingOrDeletingAttachment: boolean = false;
  isRefreshingList: boolean = false;

  dataServiceListeners: any[] = [];

  // incase a new attachment is created event back
  @Output() onAttachmentCreated: EventEmitter<any> = new EventEmitter<any>();
  @Output() onAttachmentDeleted: EventEmitter<any> = new EventEmitter<any>();
  @Output() onRemoveAttachmentHighlight: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private helperService: HelperService,
    private httpService: HTTPService,
    private dialogService: DialogService,
    public dateService: DateService,
    private formatService: FormatService,
    public attachmentService: AttachmentService,
    private cdr: ChangeDetectorRef,
    public dataService: DataService,
  ) {}

  ngOnInit() {
    this.setDataListeners();
  }
  ngOnDestroy() {
    this.dataServiceListeners.forEach((listener) => {
      try {
        listener.unsubscribe();
      } catch (err) { }
    });
  }

  setDataListeners() {
    const listener = this.dataService.getData().subscribe((obj) => {
      const data = obj.data;
      if (data.key === 'refresh-attachment') {
          this._refreshAttachmentList();
      }
    });
    this.dataServiceListeners.push(listener);
  }

  /**
   * On list lazy load
   * @param param0
   */
  onLazyLoad({ sortField, sortOrder }) {
    this._refreshAttachmentList();
  }

  /**
   * Handle on search attachment
   * @param query
   * @returns
   */
  async onSearchAttachment(query: string) {
    try {
      this.isLoading = true;

      if (!query || !query.trim()) {
        this._clearSearchFiledEffect();
        return;
      }

      const { records } = await this.attachmentService.getOrderAttachment(this.estimateId, {
        limit: 10,
        skip: 0,
        searchQuery: query.trim(),
      });

      this.searchResults = records;
    } catch (error) {
      this.helperService.showToaster('error', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * On search enter press
   * @param query
   */
  onSearchEnter(query: string) {
    if (!query) {
      this._clearSearchFiledEffect();
      return;
    }

    this.searchQuery = query.trim();
    this._refreshAttachmentList();
  }

  /**
   * On search item select
   * @param event
   */
  onSearchAttachmentSelect(attachment: any) {
    this.isSearchSelectActive = true;
    this.searchQuery = attachment.title;
    this.attachments = [attachment];
  }

  /**
   * On search query clear
   * @param event
   */
  onSearchClear(event: any) {
    this.isSearchSelectActive = false;
    this._clearSearchFiledEffect();
    this._refreshAttachmentList();
  }

  /**
   * Handle file browse
   */
  handleFileBrowse(fileUpload: HTMLElement) {
    fileUpload.click();
  }

  /**
   * Handle on create attachment
   * @param files
   */
  async onCreateAttachment(files: FileList): Promise<void> {
    try {
      if (this.isCreatingOrDeletingAttachment) return;

      this.isCreatingOrDeletingAttachment = true;

      // get first file only
      const fileToUpload = files.item(0);
      // this.fileToUpload = files.item(0);

      const formData: FormData = new FormData();
      formData.append('file', fileToUpload, fileToUpload.name);

      await this.attachmentService.createAttachment(this.estimateId, formData);

      // refresh attachment list
      this._refreshAttachmentList();

      // emit new attachment created event
      this.onAttachmentCreated.emit();
      this.cdr.detectChanges();
    } catch (error) {
      this.helperService.showToaster('error', error);
    } finally {
      this.isCreatingOrDeletingAttachment = false;
    }
  }

  /**
   * Switch attachments estimate visibility
   * @param isVisibleOnEstimate
   * @param attachmentId
   */
  async switchEstimateVisibility(attachmentId: string, isVisibleOnEstimate: boolean): Promise<void> {
    try {
      const updatedOrder = await this.httpService.updateEstimateAttachment(attachmentId, { isVisibleOnEstimate });

      // show success
      this.helperService.showToaster('success', 'Updated Successfully');
    } catch (err) {
      this.helperService.showToaster('error', err);
    }
  }

  /**
   * Switch attachments invoice visibility
   * @param isVisibleOnInvoice
   * @param attachmentId
   */
  async switchInvoiceVisibility(attachmentId: string, isVisibleOnInvoice: boolean): Promise<void> {
    try {
      const updatedOrder = await this.httpService.updateEstimateAttachment(attachmentId, { isVisibleOnInvoice });

      // show success
      this.helperService.showToaster('success', 'Updated Successfully');
    } catch (err) {
      this.helperService.showToaster('error', err);
    }
  }

  /**
   * Handle remove attachment
   * @param attachmentId
   */
  handleAttachmentDelete(attachmentId: string) {
    if (this.isCreatingOrDeletingAttachment) return;

    // set is deleting flag
    this.isCreatingOrDeletingAttachment = true;

    // handle delete
    const subs = this.attachmentService.handleAttachmentDelete(attachmentId).subscribe({
      next: (isSuccess) => {
        subs.unsubscribe();

        this.isCreatingOrDeletingAttachment = false;

        this.cdr.detectChanges();

        // if operation cancelled, return
        if (!isSuccess) return;

        // refetch all attachments
        this._refreshAttachmentList();

        // emit delete event
        this.onAttachmentDeleted.emit();
        // show success toaster
        this.helperService.showToaster('success', 'Deleted Successfully');
      },
      complete: () => {
        this.isCreatingOrDeletingAttachment = false;
      },
      error: (error) => {
        this.helperService.showToaster('error', error);
        this.isCreatingOrDeletingAttachment = false;
      },
    });
  }

  /**
   * On rename attachment
   *
   * @param attachment
   */
  onRenameAttachment(attachment: any) {
    if (this.isFinalizedRo) return;
    this.attachmentService
      .openAttachmentRenameDialog(attachment)
      .then((isSuccess) => isSuccess && this._refreshAttachmentList())
      .catch((err) => this.helperService.showToaster('error', err));
  }

  /**
   * Refresh attachment list
   */
  private async _refreshAttachmentList() {
    try {
      this.isRefreshingList = true;

      // check if attachment disabled
      this._checkIfAttachmentDisabled();

      // get attachments
      const { records, metaData } = await this.attachmentService.getOrderAttachment(this.estimateId, {
        searchQuery: this.searchQuery,
      });

      // set attachments
      this.attachments = records.map((attachment) => this._adjustAttachmentTimezone(attachment));

      this.attachments = sortBy(this.attachments, 'createdAtInMilli');

      if (this.higlightedAttachmentId) {
        // open modal here
        const filteredArray = this.attachments.filter((attachment) => attachment._id === this.higlightedAttachmentId);

        if (!filteredArray.length) {
          this.attachmentService.openAttachmentDeleteDialog();
        }

        this.onRemoveAttachmentHighlight.emit();
      }

      // set total records
      this.totalAttachments = metaData.totalRecords;
    } catch (error) {
      this.helperService.showToaster('error', error);
    } finally {
      this.isRefreshingList = false;
    }
  }

  /**
   * Check if attachment is disabled or not
   */
  private async _checkIfAttachmentDisabled() {
    try {
      this.isCheckingNewAttachmentDisabled = true;

      // get count
      const { metaData } = await this.attachmentService.getOrderAttachment(this.estimateId, {
        category: 'EXTERNAL',
        limit: 1,
        skip: 0,
      });

      // set flag
      this.isNewAttachmentCreationDisabled = metaData.totalRecords >= 15;
    } catch (error) {
      this.helperService.showToaster('error', error);
    } finally {
      this.isCheckingNewAttachmentDisabled = false;
    }
  }

  /**
   * Adjust an attachments timezone
   * @param attachment
   * @returns
   */
  private _adjustAttachmentTimezone(attachment): any {
    const adjustedAttachment = attachment;

    adjustedAttachment.date = this.dateService.getCurrentDateTimeTz(new Date(attachment.date).getTime());

    adjustedAttachment.createdAt = this.dateService.getCurrentDateTimeTz(new Date(attachment.createdAt).getTime());

    adjustedAttachment.createdAtInMilli = new Date(adjustedAttachment.createdAt).getTime();

    return adjustedAttachment;
  }

  private _clearSearchFiledEffect() {
    this.searchQuery = '';
    this.searchResults = [];
  }
}
